import { useCallback, useEffect, useState } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Config } from '../../common/config';
import AlertModal from '../../components/alertModal';
import LeftMenu from '../../components/leftMenu';
import useAxiosFetch from '../../hooks/useAxiosFetch';
import useStoreNo from '../../hooks/useStoreNo';
import { isSuccessResponse, getErrorMessage } from '../../components/utils';
import { PurchaseAccountBox, PurchasedGiftPicker, useDetachPurchase } from './purchaseAccount';

const STATE_LABEL = { READY: '대기', RUNNING: '진행중', CLOSED: '종료' };
const HISTORY_PAGE_SIZE = 10;
// [2026-10-22 F16] 종료 응답 reclaimed[].action
const RECLAIM_ACTION_LABEL = { DELETED: '무상발급 취소·수량 복구', UNTAGGED: '구매 태깅 해제', SKIPPED: '회수 안 함(사용·환불 이력)' };

function winRate(prizeCnt, missCnt) {
  if (!prizeCnt) return '-';
  const denom = prizeCnt + missCnt;
  if (!denom) return '-';
  return `${((prizeCnt / denom) * 100).toFixed(1)}%`;
}

/**
 * 상점주 GAME(현장 룰렛) 회차·경품·이력 관리.
 * 백엔드: naegift-api-promotion admin/promotion/:promotionNo/game 하위 전부.
 * operator PromotionManage/GameDetail 상당 로직을 STORE 스코프로 이식 — 발행주체는 항상
 * 이 상점(issuerType=STORE)으로 고정이라 채널 조회·issuerText 분기는 없다.
 */
export default function GameDetail() {
  const navigate = useNavigate();
  const storeNo = useStoreNo();
  const { fetchData } = useAxiosFetch();
  const [searchParams] = useSearchParams();
  const promotionNo = Number(searchParams.get('promotionNo'));

  const [promotion, setPromotion] = useState(null);
  const [game, setGame] = useState(null);
  const [rounds, setRounds] = useState([]);
  // [2026-10-22 F16] 안 뽑힌 경품(GET game.remainingPrizes)과 종료 응답의 회수 결과(reclaimed)
  const [remainingPrizes, setRemainingPrizes] = useState([]);
  const [reclaimed, setReclaimed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const [openRounds, setOpenRounds] = useState(() => new Set());

  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const showMsg = m => {
    setMsg(m);
    setMsgOpen(true);
  };

  // 수정 모달
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ roundCnt: '', missSlotCnt: '', missRewardPromotionNo: '' });
  // [2026-10-22 B-F3] 꽝 보상 쿠폰 후보 — 이 상점의 진행중 목록형 쿠폰 중 발급 이력이 없는 것(백엔드 B-1 규칙)
  const [rewardOptions, setRewardOptions] = useState([]);
  const [editBusy, setEditBusy] = useState(false);

  // 상품 등록 모달

  // 상품 삭제
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);


  // GAME 삭제
  const [gameDeleteOpen, setGameDeleteOpen] = useState(false);
  const [gameDeleteBusy, setGameDeleteBusy] = useState(false);

  // 'start' | 'next' | 'close'
  const [actionType, setActionType] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion/${promotionNo}/game`,
        method: 'get',
      });
      const data = ret?.data?.data;
      if (!isSuccessResponse(ret) || !data) {
        setLoadError('게임을 불러오지 못했습니다.');
        return;
      }
      setPromotion(data.promotion ?? null);
      setGame(data.game ?? null);
      setRounds(data.rounds ?? []);
      setRemainingPrizes(data.remainingPrizes ?? []);
    } catch (e) {
      setLoadError('게임을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionNo]);

  const loadHistory = useCallback(
    async (page = 1) => {
      try {
        const ret = await fetchData({
          url: `${Config.API_SERVER}promotion/${promotionNo}/game/history?pageNo=${page}&pageSize=${HISTORY_PAGE_SIZE}`,
          method: 'get',
        });
        const data = ret?.data?.data;
        setHistory(data?.list ?? []);
        setHistoryTotal(data?.total ?? 0);
        setHistoryTotalPages(data?.totalPage ?? 1);
      } catch (e) {
        setHistory([]);
        setHistoryTotal(0);
        setHistoryTotalPages(1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [promotionNo],
  );

  // [2026-10-27] 구매 계정이 산 기프트로 경품 추가 / 기존 칸에 추가 연결
  //  pickTarget = { mode: 'create', roundSeq } | { mode: 'attach', item }
  const [pickTarget, setPickTarget] = useState(null);
  const [purchaseAccount, setPurchaseAccount] = useState(undefined);
  const reloadAfterDetach = useCallback(() => {
    loadDetail();
  }, [loadDetail]);
  const detachPurchase = useDetachPurchase(reloadAfterDetach);

  const reloadHistoryFirstPage = useCallback(() => {
    setHistoryPage(1);
    loadHistory(1);
  }, [loadHistory]);

  useEffect(() => {
    if (!Number.isFinite(promotionNo)) return;
    loadDetail();
    loadHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promotionNo]);

  // 기본 펼침 — RUNNING 이면 진행 중인 회차, READY 면 1회차, CLOSED 면 전부 접힘.
  useEffect(() => {
    if (!game) return;
    if (game.gameState === 'RUNNING') setOpenRounds(new Set([game.curRound]));
    else if (game.gameState === 'READY') setOpenRounds(new Set([1]));
    else setOpenRounds(new Set());
  }, [game?.gameState, game?.curRound]);

  const toggleRound = roundSeq => {
    setOpenRounds(prev => {
      const next = new Set(prev);
      if (next.has(roundSeq)) next.delete(roundSeq);
      else next.add(roundSeq);
      return next;
    });
  };

  const missSlotCnt = game?.missSlotCnt ?? 0;
  const currentRound = rounds.find(r => r.roundSeq === game?.curRound);
  const hasItemsIn = roundSeq => (rounds.find(r => r.roundSeq === roundSeq)?.items?.length ?? 0) > 0;
  // [2026-10-30 B-13] 프로모션 시작일 전에는 [게임 시작]을 막는다(서버도 400)
  const startAt = promotion?.startDttm ? new Date(promotion.startDttm) : null;
  const beforeStart = !!startAt && !Number.isNaN(startAt.getTime()) && startAt.getTime() > Date.now();
  const startDateLabel = beforeStart ? `${startAt.getMonth() + 1}월 ${startAt.getDate()}일` : '';
  const canStart = hasItemsIn(1) && !beforeStart;
  const nextRoundSeq = (game?.curRound ?? 0) + 1;
  const isLastRound = nextRoundSeq > (game?.roundCnt ?? 0);
  const canGoNext = isLastRound || hasItemsIn(nextRoundSeq);
  const canEditRound = round => round?.started === false && game?.gameState !== 'CLOSED';

  // ───────── 수정 모달 ─────────

  const openEditModal = () => {
    setEditForm({
      roundCnt: String(game.roundCnt ?? ''),
      missSlotCnt: String(game.missSlotCnt ?? ''),
      missRewardPromotionNo: game.missRewardPromotionNo != null ? String(game.missRewardPromotionNo) : '',
    });
    setEditOpen(true);
    loadRewardOptions();
  };

  const loadRewardOptions = async () => {
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion?promotionType=COUPON&status=ACTIVE&pageNo=1&pageSize=100`,
        method: 'get',
      });
      const list = ret?.data?.data?.list || [];
      setRewardOptions(
        list.filter(
          p =>
            !p.couponCode &&
            // 목록형 쿠폰의 usedCount 는 "발급 매수" — 이미 나간 쿠폰은 꽝 보상으로 못 씀(지금 연결된 것은 유지)
            (Number(p.usedCount || 0) === 0 || p.promotionNo === game?.missRewardPromotionNo),
        ),
      );
    } catch (e) {
      setRewardOptions([]);
    }
  };

  // [2026-10-30 B-16] 꽝 보상 쿠폰 매수 점검 — 필요 매수 = 꽝 칸 × 남은 회차(서버도 같은 기준으로 400)
  const rewardCheck = (() => {
    if (!game || !editForm.missRewardPromotionNo) return null;
    const coupon = rewardOptions.find(p => String(p.promotionNo) === editForm.missRewardPromotionNo);
    if (!coupon || coupon.totalCount == null) return null;
    const miss = parseInt(editForm.missSlotCnt, 10) || 0;
    const roundsLeft =
      game.gameState === 'RUNNING'
        ? Math.max((game.roundCnt ?? 0) - (game.curRound ?? 0), 0)
        : parseInt(editForm.roundCnt, 10) || game.roundCnt || 0;
    const need = miss * roundsLeft;
    const left = Math.max(Number(coupon.totalCount) - Number(coupon.usedCount || 0), 0);
    return { need, left, miss, roundsLeft, short: need > left };
  })();

  const buildEditPatch = () => {
    if (!game) return {};
    const patch = {};
    const rc = parseInt(editForm.roundCnt, 10);
    if (Number.isFinite(rc) && rc !== game.roundCnt) patch.roundCnt = rc;
    const mc = parseInt(editForm.missSlotCnt, 10);
    if (Number.isFinite(mc) && mc !== game.missSlotCnt) patch.missSlotCnt = mc;
    const mr = parseInt(editForm.missRewardPromotionNo, 10);
    if (Number.isFinite(mr) && mr !== game.missRewardPromotionNo) patch.missRewardPromotionNo = mr;
    // 선택 해제(꽝 보상 없음)
    if (!editForm.missRewardPromotionNo && game.missRewardPromotionNo != null) patch.missRewardPromotionNo = null;
    return patch;
  };

  const handleEditSubmit = async () => {
    if (rewardCheck?.short) {
      showMsg(`꽝 보상 쿠폰이 부족합니다. 필요 ${rewardCheck.need}장 · 남은 ${rewardCheck.left}장. 꽝 칸을 줄이거나 보상 쿠폰 매수를 늘려 주세요.`);
      return;
    }
    const patch = buildEditPatch();
    if (Object.keys(patch).length === 0) {
      setEditOpen(false);
      return;
    }
    setEditBusy(true);
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion/${promotionNo}/game`,
        method: 'patch',
        data: patch,
      });
      if (isSuccessResponse(ret)) {
        setEditOpen(false);
        loadDetail();
      } else {
        showMsg(getErrorMessage(ret) || '수정에 실패했습니다.');
      }
    } catch (e) {
      showMsg('수정에 실패했습니다.');
    } finally {
      setEditBusy(false);
    }
  };

  // ───────── 삭제 ─────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteBusy) return;
    setDeleteBusy(true);
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion/game/item/${deleteTarget.promotionGameItemNo}`,
        method: 'delete',
      });
      setDeleteTarget(null);
      if (isSuccessResponse(ret)) {
        loadDetail();
        reloadHistoryFirstPage();
      } else {
        showMsg(getErrorMessage(ret) || '삭제에 실패했습니다.');
      }
    } catch (e) {
      showMsg('삭제에 실패했습니다.');
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleGameDeleteConfirm = async () => {
    if (gameDeleteBusy) return;
    setGameDeleteBusy(true);
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion/${promotionNo}`,
        method: 'delete',
      });
      if (isSuccessResponse(ret)) {
        navigate(`/${storeNo}/promotion-list`);
      } else {
        setGameDeleteOpen(false);
        setGameDeleteBusy(false);
        showMsg(getErrorMessage(ret) || '삭제에 실패했습니다.');
      }
    } catch (e) {
      setGameDeleteOpen(false);
      setGameDeleteBusy(false);
      showMsg('삭제에 실패했습니다.');
    }
  };

  const ACTION_PATH = { start: 'start', next: 'next-round', close: 'close' };

  const handleActionConfirm = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const ret = await fetchData({
        url: `${Config.API_SERVER}promotion/${promotionNo}/game/${ACTION_PATH[actionType]}`,
        method: 'post',
      });
      if (isSuccessResponse(ret)) {
        const reclaimedList = ret?.data?.data?.reclaimed;
        if (Array.isArray(reclaimedList)) setReclaimed(reclaimedList);
        setActionType(null);
        loadDetail();
        reloadHistoryFirstPage();
      } else {
        showMsg(getErrorMessage(ret) || '처리에 실패했습니다.');
      }
    } catch (e) {
      showMsg('처리에 실패했습니다.');
    } finally {
      setActionBusy(false);
    }
  };

  const actionTitle = () => {
    if (actionType === 'start') return '게임 시작';
    if (actionType === 'next') return '다음 회차';
    if (actionType === 'close') return '게임 강제 종료';
    return '';
  };
  // [2026-10-22 F16] 게임이 끝나면 안 뽑힌 경품은 자동 회수된다 — 종료 확인창에 개수 안내
  const reclaimNotice = () => {
    if (!remainingPrizes.length) return '';
    const free = remainingPrizes.filter(p => p.kind === 'FREE').length;
    const tagged = remainingPrizes.length - free;
    return (
      `\n\n안 뽑힌 경품 ${remainingPrizes.length}개는 종료 시 회수됩니다.` +
      (free ? `\n· 무상발급 ${free}개: 발급 취소, 기프트 수량 복구` : '') +
      (tagged ? `\n· 구매 태깅 ${tagged}개: 태깅만 해제(결제 건은 환불·재사용 가능)` : '')
    );
  };
  const actionMessage = () => {
    const cur = game?.curRound ?? 0;
    const remain = currentRound?.prizeRemain ?? 0;
    if (actionType === 'start') {
      const summary = rounds.map(r => `${r.roundSeq}회차: 경품 ${r.prizeTotal ?? 0}개`).join('\n');
      // [2026-10-22 C5] 경품 없는 회차는 다음 회차로 넘어갈 수 없다 — 시작 전에 알려준다
      const empty = rounds.filter(r => (r.items?.length ?? 0) === 0).map(r => `${r.roundSeq}회차`);
      const warn = empty.length
        ? `\n\n⚠ 경품이 없는 회차: ${empty.join(', ')} — 상품을 등록해야 그 회차로 넘어갈 수 있습니다.`
        : '';
      return `게임을 시작합니다.\n\n${summary}${warn}`;
    }
    if (actionType === 'next') {
      const next = cur + 1;
      const head =
        next > (game?.roundCnt ?? 0) ? '마지막 회차입니다. 확인하면 게임이 종료됩니다.' : `${next}회차를 시작합니다.`;
      const closing = next > (game?.roundCnt ?? 0);
      return `${head}\n현재 ${cur}회차에 경품 ${remain}개가 남아 있습니다.${closing ? reclaimNotice() : ''}`;
    }
    if (actionType === 'close') {
      return `게임을 강제 종료합니다.\n남은 회차는 진행되지 않습니다.\n현재 ${cur}회차에 경품 ${remain}개가 남아 있습니다.${reclaimNotice()}\n\n되돌릴 수 없습니다.`;
    }
    return '';
  };

  if (loading) {
    return (
      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="promotion" />
          <section className="rightcontant_box">
            <p style={{ padding: '20px 0' }}>불러오는 중...</p>
          </section>
        </section>
      </main>
    );
  }

  if (loadError || !promotion || !game) {
    return (
      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="promotion" />
          <section className="rightcontant_box">
            <p style={{ color: '#c0392b' }}>{loadError || '게임을 불러오지 못했습니다.'}</p>
            <button className="button_04 button_gray_line" onClick={() => navigate(`/${storeNo}/promotion-list`)}>
              목록으로
            </button>
          </section>
        </section>
      </main>
    );
  }

  return (
    <>
      <AlertModal open={msgOpen} close={() => setMsgOpen(false)} isMessage>
        {msg}
      </AlertModal>

      <AlertModal
        open={editOpen}
        close={() => setEditOpen(false)}
        action={handleEditSubmit}
        isMessage
        isConfirm
        isCancel
      >
        <table className="member_box">
          <tbody>
            <tr>
              <th>회차 수</th>
              <td>
                <input
                  className="input_01_01"
                  style={{ textAlign: 'right' }}
                  type="text"
                  value={editForm.roundCnt}
                  disabled={editBusy || game.gameState === 'RUNNING'}
                  onChange={e => setEditForm(f => ({ ...f, roundCnt: e.target.value.replace(/[^0-9]/g, '') }))}
                />
                {game.gameState === 'RUNNING' && (
                  <p style={{ color: '#888', fontSize: 12 }}>진행 중에는 회차 수를 변경할 수 없습니다</p>
                )}
              </td>
            </tr>
            <tr>
              <th>회차당 꽝 칸 수</th>
              <td>
                <input
                  className="input_01_01"
                  style={{ textAlign: 'right' }}
                  type="text"
                  value={editForm.missSlotCnt}
                  disabled={editBusy}
                  onChange={e => setEditForm(f => ({ ...f, missSlotCnt: e.target.value.replace(/[^0-9]/g, '') }))}
                />
                <p style={{ color: '#888', fontSize: 12 }}>
                  {game.gameState === 'RUNNING' ? '다음 회차부터 적용됩니다' : '회차마다 휠에 이 수만큼 꽝 칸이 들어갑니다'}
                </p>
              </td>
            </tr>
            {/* [2026-10-22 B-F3] 꽝 보상 쿠폰 — 번호 입력 대신 이 상점 쿠폰 중에서 선택(백엔드 B-1 복구).
                연결된 쿠폰은 앱 쿠폰받기에 안 나오고 룰렛 꽝에서만 지급된다. */}
            <tr>
              <th>꽝 보상 쿠폰 (선택)</th>
              <td>
                <select
                  className="input_01 select_01"
                  style={{ width: '100%', maxWidth: '100%' }}
                  value={editForm.missRewardPromotionNo}
                  disabled={editBusy}
                  onChange={e => setEditForm(f => ({ ...f, missRewardPromotionNo: e.target.value }))}
                >
                  <option value="">없음 — 꽝은 그냥 꽝</option>
                  {editForm.missRewardPromotionNo &&
                    !rewardOptions.some(p => String(p.promotionNo) === editForm.missRewardPromotionNo) && (
                      <option value={editForm.missRewardPromotionNo}>#{editForm.missRewardPromotionNo} (현재 연결)</option>
                    )}
                  {rewardOptions.map(p => (
                    <option key={p.promotionNo} value={String(p.promotionNo)}>
                      #{p.promotionNo} {p.name} · {Number(p.couponDiscountAmount || 0).toLocaleString()}원 · {p.totalCount ? `${p.totalCount}장` : '수량 제한 없음'}
                    </option>
                  ))}
                </select>
                {rewardCheck && (
                  <p style={{ color: rewardCheck.short ? '#d14343' : '#2e7d32', fontSize: 12, fontWeight: 600 }}>
                    필요 {rewardCheck.need}장(꽝 {rewardCheck.miss}칸 × {rewardCheck.roundsLeft}회차) · 이 쿠폰 남은 {rewardCheck.left}장
                    {rewardCheck.short ? ' — 부족해요' : ''}
                  </p>
                )}
                <p style={{ color: '#888', fontSize: 12, lineHeight: 1.5 }}>
                  꽝이 나오면 이 쿠폰이 참여자 쿠폰함에 들어갑니다. 아직 아무도 받지 않은 이 상점 쿠폰만 고를 수 있어요 —
                  꽝 보상용 쿠폰을 먼저 [프로모션 등록]에서 만들어 두세요. 연결된 쿠폰은 앱 쿠폰받기에는 나오지 않습니다.
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </AlertModal>

      <AlertModal
        open={!!deleteTarget}
        close={() => setDeleteTarget(null)}
        action={handleDeleteConfirm}
        isMessage
        isConfirm
        isCancel
      >
        {deleteTarget ? `"${deleteTarget.itemName}" (${deleteTarget.stock}개) 상품을 삭제할까요?` : ''}
      </AlertModal>

      <AlertModal
        open={gameDeleteOpen}
        close={() => setGameDeleteOpen(false)}
        action={handleGameDeleteConfirm}
        isMessage
        isConfirm
        isCancel
      >
        {`GAME 프로모션을 삭제합니다.\n\n${promotion.name}\n등록된 상품도 함께 삭제됩니다.\n\n되돌릴 수 없습니다.`}
      </AlertModal>

      <AlertModal
        open={!!actionType}
        close={() => setActionType(null)}
        action={handleActionConfirm}
        isMessage
        isConfirm
        isCancel
      >
        <h3 className="modal_popup_tit_01">{actionTitle()}</h3>
        <p style={{ whiteSpace: 'pre-line' }}>{actionMessage()}</p>
      </AlertModal>

      <section className="location_box location_box_mypage">
        <ul className="location_001">
          <li>
            <NavLink to={storeNo ? `/${storeNo}/gift-list` : '/gift-list'}>홈</NavLink>
          </li>
          <li>
            <NavLink to={`/${storeNo}/promotion-list`}>프로모션 관리</NavLink>
          </li>
          <li>회차관리</li>
        </ul>
      </section>

      <main>
        <section className="contants_box contants_box_mypage">
          <LeftMenu pageName="promotion" />

          <section className="rightcontant_box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 className="tit_01" style={{ margin: 0 }}>
                {promotion.name} <span style={{ fontSize: 13, color: '#888' }}>({STATE_LABEL[game.gameState] || game.gameState})</span>
              </h2>
              <span style={{ display: 'flex', gap: 8 }}>
                {game.gameState !== 'CLOSED' && (
                  <button className="button_04 button_gray_line" onClick={openEditModal}>
                    수정
                  </button>
                )}
                {game.gameState === 'READY' && (
                  <button className="button_04 button_gray_line" onClick={() => setGameDeleteOpen(true)}>
                    삭제
                  </button>
                )}
                <button className="button_04 button_gray_line" onClick={() => navigate(`/${storeNo}/promotion-list`)}>
                  목록으로
                </button>
              </span>
            </div>

            <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>
              {/* [2026-10-22 U5] 개발 용어(promotionNo, 슬롯) 대신 쉬운 말로 */}
              룰렛 · {game.roundCnt ?? 0}회차 중 {game.curRound ? `${game.curRound}회차` : '시작 전'} · 회차당 꽝 {missSlotCnt}칸 · 프로모션 #{promotion.promotionNo}
              {game.missRewardPromotionNo != null && <> · 꽝 보상 쿠폰 #{game.missRewardPromotionNo}</>}
            </p>

            {/* [2026-10-22 B-F4] 현장 공개 URL·QR 폐지 — 룰렛은 앱 공지사항 이벤트(로그인 회원 1인 1회). 공지는 발행처가 공지 관리에서 등록 */}
            <div style={{ marginBottom: 24, padding: '12px 14px', background: '#f6f7f9', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, lineHeight: 1.6 }}>
              <div>
                룰렛은 <b>내기프트 앱 공지사항의 이벤트</b>에서 로그인한 회원이 <b>1인 1회</b> 참여합니다.
                게임을 시작한 뒤 <b>공지 관리에서 룰렛이벤트 공지를 등록</b>해 주세요.
              </div>
              {game.participantCount != null && (
                <div style={{ marginTop: 6 }}>
                  참여 {Number(game.participantCount).toLocaleString()}명 · 당첨 {Number(game.prizeWinCount ?? 0).toLocaleString()}건 · 꽝 쿠폰 지급{' '}
                  {Number(game.rewardCouponIssuedCount ?? 0).toLocaleString()}건
                </div>
              )}
            </div>

            <div style={{ marginBottom: 24, display: 'flex', gap: 8, alignItems: 'center' }}>
              {game.gameState === 'READY' && (
                <>
                  <button className="button_05 button_black" disabled={!canStart} onClick={() => setActionType('start')}>
                    게임 시작
                  </button>
                  {beforeStart ? (
                    <span style={{ fontSize: 12, color: '#888' }}>시작일({startDateLabel}) 이후에 시작할 수 있습니다</span>
                  ) : (
                    !canStart && <span style={{ fontSize: 12, color: '#888' }}>1회차에 상품을 등록해야 시작할 수 있습니다</span>
                  )}
                </>
              )}
              {game.gameState === 'RUNNING' && (
                <>
                  <button className="button_05 button_black" disabled={!canGoNext} onClick={() => setActionType('next')}>
                    다음 회차
                  </button>
                  {!canGoNext && <span style={{ fontSize: 12, color: '#888' }}>{nextRoundSeq}회차에 상품을 등록해야 합니다</span>}
                  <button className="button_04 button_gray_line" onClick={() => setActionType('close')}>
                    게임 종료
                  </button>
                </>
              )}
              {game.gameState === 'CLOSED' && <span style={{ fontSize: 12, color: '#888' }}>종료된 게임입니다</span>}
            </div>

            {reclaimed && (
              <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 10, marginBottom: 16, fontSize: 13 }}>
                <strong>안 뽑힌 경품 회수 {reclaimed.length}건</strong>
                {reclaimed.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {reclaimed.map(r => (
                      <li key={r.userGiftNo}>
                        {r.roundSeq}회차 {r.itemName} — {r.giftName || `기프트 #${r.giftNo}`} (선물 #{r.userGiftNo}) ·{' '}
                        {RECLAIM_ACTION_LABEL[r.action] || r.action}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {game.gameState === 'CLOSED' && !reclaimed && remainingPrizes.length > 0 && (
              <p style={{ fontSize: 12, color: '#c00', marginBottom: 16 }}>
                회수되지 않은 경품 {remainingPrizes.length}개가 남아 있습니다. 내기프트 운영팀에 회수를 요청해 주세요.
              </p>
            )}

            {/* [2026-10-27] 룰렛 경품 구매 계정 — 내 상점 1개. 연결한 계정이 산 기프트로 경품을 등록한다 */}
            <PurchaseAccountBox onChange={setPurchaseAccount} />

            {/* [2026-10-28] 회차별 상품 · 뽑기 이력을 테두리 카드로(상점 정보 관리와 같은 틀) */}
            <article className="form_card list_card">
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>회차별 상품</h3>
            {/* [2026-10-28] 버튼 뜻을 쉬운 말로 한 줄 안내 */}
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              경품은 구매 계정으로 사 둔 기프트로 넣어요. 회차를 시작하기 전까지 [개수 늘리기] · [개수 줄이기] · [삭제]로 바꿀 수 있고, 줄이거나 삭제한 기프트는 구매 계정으로 돌아가 환불하거나 다른 룰렛에 쓸 수 있어요.
            </p>
            {rounds.map(round => {
              const isOpen = openRounds.has(round.roundSeq);
              const isCurrent = game.gameState === 'RUNNING' && round.roundSeq === game.curRound;
              const editable = canEditRound(round);
              return (
                <div key={round.roundSeq} style={{ marginBottom: isOpen ? 28 : 8 }}>
                  <div
                    onClick={() => toggleRound(round.roundSeq)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, cursor: 'pointer', flexWrap: 'wrap' }}
                  >
                    <span style={{ fontSize: 12, transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                    <strong style={{ fontSize: 15 }}>{round.roundSeq}회차</strong>
                    {isCurrent && <span style={{ fontSize: 12, color: '#2c6b45' }}>진행중</span>}
                    <span style={{ fontSize: 12, color: '#888' }}>
                      경품 {round.prizeUsed ?? 0} / {round.prizeTotal ?? 0} · 남은 {round.prizeRemain ?? 0}
                    </span>
                    <span style={{ fontSize: 12, color: '#888' }}>당첨률 {winRate(round.prizeTotal ?? 0, missSlotCnt)}</span>
                    {round.started && <span style={{ fontSize: 12, color: '#888' }}>시작된 회차는 상품을 변경할 수 없습니다</span>}
                    {editable && (
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                        <button
                          className="button_04 button_red"
                          onClick={() => setPickTarget({ mode: 'create', roundSeq: round.roundSeq })}
                        >
                          + 경품 추가
                        </button>
                      </span>
                    )}
                  </div>

                  {isOpen && (
                    <table className="table_box_01" style={{ minWidth: 0, marginTop: 8 }}>
                      <thead>
                        <tr>
                          <th>상품명</th>
                          <th>연결 기프트</th>
                          <th>배정 슬롯</th>
                          <th>당첨</th>
                          <th>남은</th>
                          <th>상태</th>
                          <th>관리</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(round.items ?? []).length === 0 ? (
                          <tr>
                            <td colSpan={7} style={{ textAlign: 'center' }}>
                              등록된 상품이 없습니다.
                            </td>
                          </tr>
                        ) : (
                          round.items.map(item => {
                            const remain = (item.stock ?? 0) - (item.usedCnt ?? 0);
                            const hasGift = item.giftNo != null;
                            return (
                              <tr key={item.promotionGameItemNo}>
                                <td>{item.itemName}</td>
                                <td>{hasGift ? `#${item.giftNo}` : <span style={{ color: '#888', fontSize: 12 }}>실물경품</span>}</td>
                                <td>{item.stock}</td>
                                <td>{item.usedCnt ?? 0}</td>
                                <td>{remain}</td>
                                <td>
                                  <span className={`state-badge ${remain <= 0 ? 'promo-ENDED' : 'promo-ACTIVE'}`}>
                                    {remain <= 0 ? '소진' : '가능'}
                                  </span>
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {/* [2026-10-30] 시작된 회차는 서버가 변경을 막으므로 버튼을 숨긴다 */}
                                  {editable ? (
                                    <>
                                      <button
                                        className="button_04 button_gray_line"
                                        style={{ marginRight: 4 }}
                                        disabled={!hasGift}
                                        onClick={() => setPickTarget({ mode: 'attach', item })}
                                      >
                                        개수 늘리기
                                      </button>
                                      <button
                                        className="button_04 button_gray_line"
                                        style={{ marginRight: 4 }}
                                        disabled={!hasGift}
                                        onClick={() => detachPurchase(item)}
                                      >
                                        개수 줄이기
                                      </button>
                                      <button className="button_04 button_gray_line" onClick={() => setDeleteTarget(item)}>
                                        삭제
                                      </button>
                                    </>
                                  ) : (
                                    <span style={{ fontSize: 12, color: '#aaa' }}>-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}

            {pickTarget && (
              <PurchasedGiftPicker
                promotionNo={promotionNo}
                target={pickTarget}
                accountLinked={!!purchaseAccount}
                onClose={() => setPickTarget(null)}
                onDone={() => {
                  setPickTarget(null);
                  loadDetail();
                  reloadHistoryFirstPage();
                }}
              />
            )}
            </article>

            <article className="form_card list_card">
            <h3 style={{ marginTop: 0, marginBottom: 8 }}>뽑기 이력</h3>
            <table className="table_box_01" style={{ minWidth: 0, marginTop: 8 }}>
              <thead>
                <tr>
                  <th>회차</th>
                  <th>순번</th>
                  <th>결과</th>
                  <th>상품명</th>
                  <th>전화번호</th>
                  <th>시각</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center' }}>
                      아직 뽑기 이력이 없습니다.
                    </td>
                  </tr>
                ) : (
                  history.map(h => (
                    <tr key={h.drawNo}>
                      <td>{h.roundSeq}회차</td>
                      <td>{h.drawSeq}</td>
                      <td>
                        <span className={`state-badge ${h.resultType === 'PRIZE' ? 'promo-ACTIVE' : 'promo-ENDED'}`}>
                          {h.resultType === 'PRIZE' ? '당첨' : '꽝'}
                        </span>
                      </td>
                      <td>{h.itemName || '-'}</td>
                      <td>{h.phoneNo || '-'}</td>
                      <td>{h.drawDttm ? new Date(h.drawDttm).toLocaleString('ko-KR') : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <p style={{ fontSize: 12, color: '#888', margin: '8px 0' }}>전체 개수: {historyTotal}건</p>
            {historyTotalPages > 1 && (
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({ length: historyTotalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    className={p === historyPage ? 'button_04 button_black' : 'button_04 button_gray_line'}
                    onClick={() => {
                      setHistoryPage(p);
                      loadHistory(p);
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
            </article>
          </section>
        </section>
      </main>
    </>
  );
}
